import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { toast } from 'sonner';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@staff-app/ui';
import { STAFF_PROXY_PUSH_TO_SCREENS } from '@staff-app/graphql-client';
import { useDialogSubmitState } from '@/hooks/useDialogSubmitState';

interface Props {
  open: boolean;
  deviceIds: string[];
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

interface PushResult {
  staffProxyPushToScreens: string;
}

/**
 * Assign one content id (asset/playlist/schedule, auto-detected) to every
 * selected device and push it to their screens via open-api. Loops the
 * per-device staffProxyPushToScreens mutation.
 */
export function BulkAssignDialog({ open, deviceIds, onOpenChange, onSuccess }: Props) {
  const [contentId, setContentId] = useState('');

  useEffect(() => {
    if (!open) setContentId('');
  }, [open]);

  const [setMutation, { loading }] = useMutation<PushResult>(STAFF_PROXY_PUSH_TO_SCREENS, {
    refetchQueries: ['staffAgentDevices'],
  });

  const trimmed = contentId.trim();
  const count = deviceIds.length;
  const { disabled: submitDisabled, disabledTooltip } = useDialogSubmitState({
    isSubmitting: loading,
    isRequiredFilled: !!trimmed && count > 0,
  });

  const handleSubmit = async () => {
    if (!trimmed || !count) return;
    // Assign every device independently so one failure doesn't skip the rest.
    const results = await Promise.allSettled(
      deviceIds.map((deviceId) => setMutation({ variables: { deviceId, contentId: trimmed } })),
    );
    const failed = results.filter((r) => r.status === 'rejected').length;
    const succeeded = count - failed;

    if (failed === 0) {
      toast.success(`Pushed to ${succeeded} device${succeeded === 1 ? '' : 's'}`, {
        description: 'Content assigned and pushed to the screens (type auto-detected).',
      });
      onSuccess?.();
      onOpenChange(false);
      return;
    }

    // Partial/total failure: surface counts + the first error, keep the dialog
    // open so the operator can correct the id and retry the rest.
    const firstErr = results.find((r) => r.status === 'rejected') as
      | PromiseRejectedResult
      | undefined;
    const msg = (firstErr?.reason as { message?: string })?.message ?? 'Unknown error';
    toast.error(`Assigned ${succeeded} of ${count}`, {
      description: `${failed} failed: ${msg}`,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bulk-assign-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {'Assign content to '}
            {count} {count === 1 ? 'device' : 'devices'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="bulk-assign-content-id">{'Content ID'}</Label>
            <Input
              id="bulk-assign-content-id"
              placeholder="asset / playlist / schedule id"
              value={contentId}
              onChange={(e) => setContentId(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !submitDisabled) handleSubmit();
              }}
              data-testid="bulk-assign-input"
              autoComplete="off"
              autoFocus
            />
            <p className="text-sm text-muted-foreground">
              {'Type is detected automatically and pushed to every selected device via open-api.'}
            </p>
          </div>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">{'Cancel'}</Button>
          </DialogClose>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={submitDisabled ? 0 : -1}>
                  <Button onClick={handleSubmit} disabled={submitDisabled} data-testid="bulk-assign-submit">
                    {loading ? 'Pushing…' : `Push to ${count}`}
                  </Button>
                </span>
              </TooltipTrigger>
              {disabledTooltip ? <TooltipContent>{disabledTooltip}</TooltipContent> : null}
            </Tooltip>
          </TooltipProvider>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
